import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { useLiveEvents } from "@/hooks/useLiveEvents";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Route as RouteIcon, Rocket, Archive, FlaskConical, Pencil, Loader2, Info } from "lucide-react";

const KINDS = ["welcome", "onboarding", "support", "lead", "followup", "payment", "custom"];
const TRIGGERS = [["start", "/start (with optional parameter)"], ["command", "Command"], ["message", "Message received"],
  ["callback", "Callback button"], ["new_chat_member", "New chat member"], ["chat_join_request", "Chat join request"], ["manual", "Manual / test only"]];
const BEHAVIOURS = {
  continue_existing: "Continue an unfinished flow; otherwise run first-time onboarding once (recommended)",
  first_time_only: "Run only the first time a user ever presses /start",
  restart_always: "Restart the welcome flow on every /start",
  start_selected_again: "Cancel the current flow and start the selected one again",
};

const Card = ({ children, className = "" }) => <div className={`bg-card border border-border rounded-lg p-5 ${className}`}>{children}</div>;

/** Bot detail → Welcome Flow: the journeys this bot runs after /start (and other triggers). */
export default function WelcomeFlowTab({ bot }) {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [settings, setSettings] = useState(null);
  const [open, setOpen] = useState(false);
  const [testFor, setTestFor] = useState(null);
  const [form, setForm] = useState({ name: "Welcome Flow", kind: "welcome", trigger_type: "start", trigger_value: "" });
  const [chatId, setChatId] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => Promise.all([
    api.get(`/bots/${bot.id}/workflows`).then((r) => setItems(r.data.items)),
    api.get(`/bots/${bot.id}/journey-settings`).then((r) => setSettings(r.data)),
  ]).catch((e) => { toast.error(apiErr(e)); setItems([]); }), [bot.id]);
  useEffect(() => { load(); }, [load]);
  useLiveEvents(["journey.updated"], () => load());

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/bots/${bot.id}/workflows`, { name: form.name, kind: form.kind,
        trigger: { type: form.trigger_type, value: form.trigger_value || null } });
      toast.success("Flow created", { description: "Opening the builder." });
      setOpen(false);
      navigate(`/bots/${bot.id}/flows/${data.id}`);
    } catch (e) { toast.error(apiErr(e)); }
    setBusy(false);
  };
  const publish = async (w) => {
    try { await api.post(`/workflows/${w.id}/publish`); toast.success(`Published ${w.name}`); load(); }
    catch (e) { const d = e?.response?.data?.detail; toast.error(typeof d === "object" ? d.message : apiErr(e), { description: Array.isArray(d?.errors) ? d.errors.slice(0, 3).join(" · ") : undefined }); }
  };
  const archive = async (w) => {
    if (!window.confirm(`Archive "${w.name}"? Users already inside it finish on their current version; new users won't enter it.`)) return;
    try { await api.delete(`/workflows/${w.id}`); toast.success("Archived"); load(); } catch (e) { toast.error(apiErr(e)); }
  };
  const runTest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/workflows/${testFor.id}/test`, { chat_id: Number(chatId), use_draft: true });
      toast.success("Test started against the real bot", { description: "Watch it live in the run view." });
      setTestFor(null);
      navigate(`/journeys/${data.run.id}`);
    } catch (e) { const d = e?.response?.data?.detail; toast.error(typeof d === "object" ? d.message : apiErr(e), { description: Array.isArray(d?.errors) ? d.errors.slice(0, 3).join(" · ") : undefined }); }
    setBusy(false);
  };
  const saveBehaviour = async (v) => {
    try { await api.put(`/bots/${bot.id}/journey-settings`, { start_behaviour: v }); setSettings((s) => ({ ...s, start_behaviour: v })); toast.success("/start behaviour saved"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  // webhook_secret is never sent to the browser; runtime_mode is the public signal.
  const webhookOk = bot.runtime_mode === "webhook";

  return (
    <div className="space-y-5">
      {!webhookOk && (
        <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/20 text-amber-600 dark:text-amber-400 rounded-lg p-3 text-sm" data-testid="journey-webhook-notice">
          <Info size={16} className="mt-0.5 shrink-0" />
          <span>This bot's update webhook isn't registered, so Telegram won't deliver <span className="mono">/start</span> to BotControl and no flow can trigger. Register it from the <b>Webhook</b> tab.</span>
        </div>
      )}
      <Card>
        <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
          <div className="flex items-center gap-2"><RouteIcon size={18} className="text-primary" /><h3 className="font-semibold">Journeys</h3>
            <span className="text-xs text-muted-foreground">Automated sequences that run after <span className="mono">/start</span>, a command, a button or a join.</span></div>
          <Button size="sm" className="gap-1.5" onClick={() => setOpen(true)} data-testid="btn-new-flow"><Plus size={14} /> New flow</Button>
        </div>
        {!items ? <Skeleton className="h-24 w-full" /> : items.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center">No flows yet. Create a Welcome Flow and every new user gets it automatically.</div>
        ) : (
          <div className="divide-y divide-border mt-3">
            {items.map((w) => (
              <div key={w.id} className="flex items-center gap-3 py-3 flex-wrap" data-testid={`flow-row-${w.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap"><span className="font-medium">{w.name}</span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-secondary capitalize">{w.kind}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border ${w.status === "published" ? "text-emerald-500 bg-emerald-500/10 border-emerald-500/20" : w.status === "archived" ? "text-muted-foreground border-border" : "text-amber-500 bg-amber-500/10 border-amber-500/20"}`}>
                      {w.status === "published" ? `Published v${w.published?.version}` : w.status}</span>
                    {w.draft && w.status === "published" && w.draft.version > (w.published?.version || 0) && <span className="text-[10px] text-muted-foreground">draft v{w.draft.version} unpublished</span>}
                  </div>
                  <div className="text-xs text-muted-foreground mono">trigger: {w.trigger?.type}{w.trigger?.value ? ` "${w.trigger.value}"` : ""} · {w.published?.node_count ?? w.draft?.node_count ?? 0} steps · {w.open_runs} user{w.open_runs === 1 ? "" : "s"} in flow</div>
                </div>
                <div className="flex items-center gap-1.5">
                  <Button size="sm" variant="outline" className="gap-1.5" onClick={() => navigate(`/bots/${bot.id}/flows/${w.id}`)} data-testid={`btn-edit-flow-${w.id}`}><Pencil size={13} /> Edit</Button>
                  <Button size="sm" variant="outline" className="gap-1.5" onClick={() => { setTestFor(w); setChatId(""); }} title="Run the draft against the real bot into a chat you choose"><FlaskConical size={13} /> Test</Button>
                  {w.status !== "archived" && <Button size="sm" className="gap-1.5" onClick={() => publish(w)} data-testid={`btn-publish-flow-${w.id}`}><Rocket size={13} /> Publish</Button>}
                  {w.status !== "archived" && <Button size="sm" variant="ghost" className="text-rose-500" onClick={() => archive(w)} title="Archive"><Archive size={13} /></Button>}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="max-w-2xl">
        <h3 className="font-semibold mb-1">/start behaviour</h3>
        <p className="text-xs text-muted-foreground mb-3">What happens when a user presses /start again.</p>
        {!settings ? <Skeleton className="h-9 w-full" /> : (
          <Select value={settings.start_behaviour} onValueChange={saveBehaviour}><SelectTrigger data-testid="select-start-behaviour"><SelectValue /></SelectTrigger>
            <SelectContent>{settings.options.map((o) => <SelectItem key={o} value={o}>{BEHAVIOURS[o] || o}</SelectItem>)}</SelectContent></Select>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}><DialogContent>
        <DialogHeader><DialogTitle>New flow</DialogTitle><DialogDescription>You'll design the steps visually next.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="input-flow-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Kind</Label><Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{KINDS.map((k) => <SelectItem key={k} value={k} className="capitalize">{k}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Trigger</Label><Select value={form.trigger_type} onValueChange={(v) => setForm({ ...form, trigger_type: v })}><SelectTrigger data-testid="select-flow-trigger"><SelectValue /></SelectTrigger><SelectContent>{TRIGGERS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select></div>
          </div>
          {["start", "command", "callback", "message"].includes(form.trigger_type) && (
            <div><Label>{form.trigger_type === "start" ? "Start parameter (blank = default welcome)" : form.trigger_type === "command" ? "Command (without /)" : form.trigger_type === "callback" ? "Callback data" : "Keyword / regex (blank = any message)"}</Label>
              <Input className="mono" value={form.trigger_value} onChange={(e) => setForm({ ...form, trigger_value: e.target.value })} placeholder={form.trigger_type === "start" ? "leave empty for every /start" : ""} />
              {form.trigger_type === "start" && <p className="text-[11px] text-muted-foreground mt-1">{form.trigger_value ? <><b>Only</b> runs for <span className="mono">/start {form.trigger_value}</span> (deep link <span className="mono">t.me/bot?start={form.trigger_value}</span>). A plain <span className="mono">/start</span> will NOT trigger it unless another flow has no parameter.</> : <>Empty = this flow runs for every <span className="mono">/start</span>. Set a parameter only to target a specific deep link.</>}</p>}</div>
          )}
        </div>
        <DialogFooter><Button onClick={create} disabled={busy || !form.name.trim()} data-testid="btn-create-flow">{busy ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Create &amp; open builder</Button></DialogFooter>
      </DialogContent></Dialog>

      <Dialog open={!!testFor} onOpenChange={(o) => !o && setTestFor(null)}><DialogContent>
        <DialogHeader><DialogTitle>Test "{testFor?.name}"</DialogTitle><DialogDescription>Runs the current draft against the real bot into the chat you choose. Test runs are marked and never appear in production analytics.</DialogDescription></DialogHeader>
        <div><Label>Your Telegram chat / user id</Label><Input className="mono" value={chatId} onChange={(e) => setChatId(e.target.value.replace(/[^0-9-]/g, ""))} placeholder="e.g. 8209166916" data-testid="input-test-chat" />
          <p className="text-xs text-muted-foreground mt-1">Send the bot any message first so it can reply to you, then enter your numeric id (visible in Users).</p></div>
        <DialogFooter><Button onClick={runTest} disabled={busy || !chatId} className="gap-1.5" data-testid="btn-run-test">{busy ? <Loader2 size={15} className="animate-spin" /> : <FlaskConical size={15} />} Run test</Button></DialogFooter>
      </DialogContent></Dialog>
    </div>
  );
}
